import { useMutation, useQueryClient } from '@tanstack/react-query';
import api from '@/lib/api';
import type {
    AuthResponse,
    LoginFormData,
    RegisterFormData,
    RequestPasswordResetFormData,
    ResetPasswordFormData,
} from '@/types/auth';

// API functions
export const authApi = {
    login: async (data: LoginFormData): Promise<AuthResponse> => {
        const response = await api.post('/auth/login', data);
        return response.data;
    },

    register: async (data: RegisterFormData): Promise<AuthResponse> => {
        const response = await api.post('/auth/register', data);
        return response.data;
    },

    refreshToken: async (refreshToken: string): Promise<AuthResponse> => {
        const response = await api.post('/auth/refresh', { refreshToken });
        return response.data;
    },

    requestPasswordReset: async (data: RequestPasswordResetFormData): Promise<void> => {
        await api.post('/auth/request-password-reset', data);
    },

    resetPassword: async (data: ResetPasswordFormData): Promise<void> => {
        await api.post('/auth/reset-password', data);
    },

    logout: async (): Promise<void> => {
        await api.post('/auth/logout');
    },
};

// React Query hooks
export const useLogin = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: authApi.login,
        onSuccess: (data) => {
            // Store tokens
            localStorage.setItem('accessToken', data.accessToken);
            localStorage.setItem('refreshToken', data.refreshToken);

            // Invalidate and refetch user data
            queryClient.invalidateQueries({ queryKey: ['user'] });
        },
    });
};

export const useRegister = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: authApi.register,
        onSuccess: (data) => {
            // Store tokens
            localStorage.setItem('accessToken', data.accessToken);
            localStorage.setItem('refreshToken', data.refreshToken);

            // Invalidate and refetch user data
            queryClient.invalidateQueries({ queryKey: ['user'] });
        },
    });
};

export const useRequestPasswordReset = () => {
    return useMutation({
        mutationFn: authApi.requestPasswordReset,
    });
};

export const useResetPassword = () => {
    return useMutation({
        mutationFn: authApi.resetPassword,
    });
};

export const useLogout = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: authApi.logout,
        onSuccess: () => {
            // Clear tokens
            localStorage.removeItem('accessToken');
            localStorage.removeItem('refreshToken');

            // Clear all cached data
            queryClient.clear();
        },
    });
};
